import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRightLeft,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { parsePipelineStages } from '../../lib/crm/opportunityAutomationContext';
import {
  markOpportunityFollowUpComplete,
  scheduleOpportunityFollowUp,
} from '../../lib/crm/opportunityFollowUp';
import { moveOpportunityToStage } from '../../lib/crm/opportunityStage';
import { isLostReasonRequired, setOpportunityStatus } from '../../lib/crm/opportunityStatus';
import OpportunityFollowUpSchedulePanel, {
  followUpInHours,
} from '../crm/OpportunityFollowUpSchedulePanel';
import LostReasonModal from '../LostReasonModal';

const MENU_WIDTH = 278;
const VIEWPORT_MARGIN = 8;

/** Anchor for the menu — the pressed row's on-screen box. */
export interface QuickActionsAnchor {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface CrmLeadQuickActionsMenuProps {
  businessId: string;
  opportunityId: number;
  leadTitle: string;
  anchor: QuickActionsAnchor;
  onClose: () => void;
  /** Called after any successful change so the caller can refresh its list. */
  onChanged: () => void;
}

interface LeadState {
  customerId: string | null;
  status: string | null;
  pipelineId: number | null;
  stageId: number | null;
  nextFollowUpAt: string | null;
}

type Stage = { id: number; name: string };

type View = 'root' | 'followUp';

/** Status dot, matching the Open/Won/Lost dots used across the CRM. */
function StatusDot({ className }: { className: string }) {
  return (
    <span className="flex h-[18px] w-[18px] items-center justify-center">
      <span className={`h-2.5 w-2.5 rounded-full ${className}`} />
    </span>
  );
}

function MenuButton({
  icon,
  label,
  detail,
  onClick,
  disabled,
  tone = 'default',
  trailing,
}: {
  icon: React.ReactNode;
  label: string;
  detail?: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'win' | 'loss';
  trailing?: React.ReactNode;
}) {
  const toneClass =
    tone === 'win' ? 'text-emerald-700' : tone === 'loss' ? 'text-red-700' : 'text-gray-800';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 touch-manipulation"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className={`shrink-0 ${toneClass}`}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm font-medium ${toneClass}`}>{label}</span>
        {detail ? (
          <span className="block truncate text-[11px] text-gray-500">{detail}</span>
        ) : null}
      </span>
      {trailing ?? null}
    </button>
  );
}

/**
 * Long-press quick actions for a CRM lead row: schedule/complete a follow-up,
 * mark won or lost, or move stage — without opening the full lead drawer.
 *
 * Every mutation goes through the shared CRM libs so the activity log and
 * enter-stage automations behave exactly like the CRM board.
 */
export default function CrmLeadQuickActionsMenu({
  businessId,
  opportunityId,
  leadTitle,
  anchor,
  onClose,
  onChanged,
}: CrmLeadQuickActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>('root');
  const [stagesOpen, setStagesOpen] = useState(false);
  const [lead, setLead] = useState<LeadState | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingLead, setLoadingLead] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customDraft, setCustomDraft] = useState('');
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Place next to the pressed row, flipping above when there isn't room below.
  useLayoutEffect(() => {
    const menuHeight = menuRef.current?.offsetHeight ?? 240;
    const spaceBelow = window.innerHeight - (anchor.top + anchor.height);
    const top =
      spaceBelow >= menuHeight + VIEWPORT_MARGIN
        ? anchor.top + anchor.height + 6
        : Math.max(VIEWPORT_MARGIN, anchor.top - menuHeight - 6);
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, anchor.left),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN)
    );
    setPosition({ top, left });
  }, [anchor, view, stagesOpen, stages.length, lead]);

  useEffect(() => {
    let cancelled = false;
    const loadLead = async () => {
      try {
        const { data, error } = await supabase
          .from('pipeline_opportunities')
          .select('customer_id, status, pipeline_id, stage_id, next_follow_up_at')
          .eq('id', opportunityId)
          .maybeSingle();
        if (error) throw error;
        if (cancelled || !data) return;

        setLead({
          customerId: data.customer_id ?? null,
          status: data.status ?? null,
          pipelineId: data.pipeline_id ?? null,
          stageId: data.stage_id ?? null,
          nextFollowUpAt: data.next_follow_up_at ?? null,
        });

        if (data.pipeline_id) {
          const { data: pipeline } = await supabase
            .from('pipelines')
            .select('stages')
            .eq('id', data.pipeline_id)
            .maybeSingle();
          if (!cancelled && pipeline) setStages(parsePipelineStages(pipeline.stages));
        }
      } catch (err) {
        console.error('Error loading lead for quick actions:', err);
      } finally {
        if (!cancelled) setLoadingLead(false);
      }
    };
    void loadLead();
    return () => {
      cancelled = true;
    };
  }, [opportunityId]);

  // Escape, outside click, and scroll all dismiss (the row lives in a scroller).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (lostModalOpen) return;
      onClose();
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (lostModalOpen) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onScroll = () => {
      if (!lostModalOpen) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('touchstart', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose, lostModalOpen]);

  const finish = useCallback(
    (message: string) => {
      toast.success(message);
      onChanged();
      onClose();
    },
    [onChanged, onClose]
  );

  const runAction = useCallback(
    async (action: () => Promise<void>, successMessage: string, failureMessage: string) => {
      if (saving) return;
      setSaving(true);
      try {
        await action();
        finish(successMessage);
      } catch (err) {
        console.error(failureMessage, err);
        toast.error(failureMessage);
        setSaving(false);
      }
    },
    [saving, finish]
  );

  const setStatus = (toStatus: 'Won' | 'Lost', lostReason: string | null = null) =>
    runAction(
      () =>
        setOpportunityStatus({
          businessId,
          opportunityId,
          customerId: lead?.customerId,
          fromStatus: lead?.status,
          toStatus,
          lostReason,
        }),
      toStatus === 'Won' ? 'Lead marked won' : 'Lead marked lost',
      `Could not mark the lead ${toStatus.toLowerCase()}`
    );

  const handleLostClick = async () => {
    const needsReason = await isLostReasonRequired(businessId);
    if (needsReason) {
      setLostModalOpen(true);
      return;
    }
    void setStatus('Lost');
  };

  const saveFollowUp = (followUpAt: string) =>
    runAction(
      () =>
        scheduleOpportunityFollowUp({
          businessId,
          opportunityId,
          customerId: lead?.customerId,
          followUpAt,
          previousFollowUpAt: lead?.nextFollowUpAt ?? null,
        }),
      'Follow-up scheduled',
      'Could not schedule the follow-up'
    );

  const completeFollowUp = () =>
    runAction(
      () =>
        markOpportunityFollowUpComplete({
          businessId,
          opportunityId,
          customerId: lead?.customerId,
          previousFollowUpAt: lead?.nextFollowUpAt ?? null,
        }),
      'Follow-up marked done',
      'Could not complete the follow-up'
    );

  const moveStage = (stage: Stage) =>
    runAction(
      () =>
        moveOpportunityToStage({
          businessId,
          opportunityId,
          customerId: lead?.customerId,
          oldStageId: lead?.stageId ?? 0,
          newStageId: stage.id,
          oldStageName: stages.find((s) => s.id === lead?.stageId)?.name ?? null,
          newStageName: stage.name,
        }),
      `Moved to ${stage.name}`,
      'Could not move the lead'
    );

  const followUpLabel = lead?.nextFollowUpAt
    ? `Due ${new Date(lead.nextFollowUpAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}`
    : undefined;
  const currentStageName = stages.find((s) => s.id === lead?.stageId)?.name;

  return createPortal(
    <>
      <div
        ref={menuRef}
        role="menu"
        aria-label="Lead quick actions"
        className="fixed z-[100000] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_18px_50px_rgba(0,0,0,0.22)]"
        style={{
          width: MENU_WIDTH,
          top: position?.top ?? -9999,
          left: position?.left ?? -9999,
          visibility: position ? 'visible' : 'hidden',
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-2">
          {view !== 'root' ? (
            <button
              type="button"
              onClick={() => setView('root')}
              className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Back"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          ) : null}
          <p className="min-w-0 flex-1 truncate text-[12px] font-semibold text-gray-900">
            {view === 'followUp' ? 'Set follow-up' : leadTitle}
          </p>
          {saving ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" /> : null}
        </div>

        {view === 'root' ? (
          <div className="p-1">
            {/* Follow-up: reschedule and "done" share one line. */}
            <div className="flex items-stretch gap-1">
              <div className="min-w-0 flex-1">
                <MenuButton
                  icon={<CalendarClock className="h-[18px] w-[18px]" />}
                  // Shortened when the Done button shares the line.
                  label={lead?.nextFollowUpAt ? 'Reschedule' : 'Set follow-up'}
                  detail={followUpLabel}
                  onClick={() => setView('followUp')}
                  disabled={saving}
                  trailing={
                    lead?.nextFollowUpAt ? undefined : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                    )
                  }
                />
              </div>
              {lead?.nextFollowUpAt ? (
                <button
                  type="button"
                  onClick={() => void completeFollowUp()}
                  disabled={saving}
                  title="Mark follow-up done"
                  className="my-0.5 flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 px-2 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Check className="h-3.5 w-3.5" />
                  Done
                </button>
              ) : null}
            </div>

            <MenuButton
              icon={<StatusDot className="bg-emerald-500" />}
              label="Mark won"
              onClick={() => void setStatus('Won')}
              disabled={saving || lead?.status === 'Won'}
              tone="win"
            />
            <MenuButton
              icon={<StatusDot className="bg-red-500" />}
              label="Mark lost"
              onClick={() => void handleLostClick()}
              disabled={saving || lead?.status === 'Lost'}
              tone="loss"
            />

            {/* Move stage expands in place rather than pushing a second screen. */}
            <MenuButton
              icon={<ArrowRightLeft className="h-[18px] w-[18px]" />}
              label="Move stage"
              detail={currentStageName}
              onClick={() => setStagesOpen((open) => !open)}
              disabled={saving}
              trailing={
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-300 transition-transform ${
                    stagesOpen ? 'rotate-180' : ''
                  }`}
                />
              }
            />
            {stagesOpen ? (
              <div className="mb-1 ml-3 max-h-[200px] overflow-y-auto border-l border-gray-100 pl-1.5 ios-momentum-scroll">
                {stages.length === 0 ? (
                  loadingLead ? (
                    <div className="flex justify-center py-2.5">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-300" />
                    </div>
                  ) : (
                    <p className="px-2.5 py-2 text-[12px] text-gray-500">
                      No stages on this pipeline.
                    </p>
                  )
                ) : (
                  stages.map((stage) => {
                    const isCurrent = stage.id === lead?.stageId;
                    return (
                      <button
                        key={stage.id}
                        type="button"
                        onClick={() => !isCurrent && void moveStage(stage)}
                        disabled={saving || isCurrent}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors touch-manipulation ${
                          isCurrent
                            ? 'cursor-default bg-blue-50 font-semibold text-blue-700'
                            : 'font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <span className="min-w-0 truncate">{stage.name}</span>
                        {isCurrent ? (
                          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide">
                            Current
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {view === 'followUp' ? (
          <div className="p-3">
            <OpportunityFollowUpSchedulePanel
              customDraft={customDraft}
              saving={saving}
              onCustomDraftChange={setCustomDraft}
              onQuickSelect={(hours) => void saveFollowUp(followUpInHours(hours))}
              onCustomCommit={(value) => {
                const parsed = new Date(value);
                if (Number.isNaN(parsed.getTime())) {
                  toast.error('Pick a valid date and time');
                  return;
                }
                void saveFollowUp(parsed.toISOString());
              }}
              title="Follow up in"
            />
          </div>
        ) : null}

      </div>

      {lostModalOpen ? (
        <LostReasonModal
          isOpen
          businessId={businessId}
          onConfirm={(reason) => {
            setLostModalOpen(false);
            void setStatus('Lost', reason);
          }}
          onSkip={() => {
            setLostModalOpen(false);
            void setStatus('Lost');
          }}
          onCancel={() => setLostModalOpen(false)}
        />
      ) : null}
    </>,
    document.body
  );
}
